/**
 * The anchored rental's unlock attempts (ant's Rental.unlocks) as the panel
 * lists them. ant returns them as Json grouped per unlockable unit,
 * `{hardware_id, unlocks: [{id, timestamp, type: "attempt" | "success"}]}`,
 * oldest first; they are flattened here, newest first, each named by its unit.
 */

import { RentalUnlocks } from '../../hooks/chat/api';

export interface UnlockEvent {
  id: string;
  at: string;
  /** "attempt" or "success" (anything else ant may add, as it comes). */
  type: string;
  hardwareLabel: string;
}

export interface UnlockSummary {
  attempts: number;
  successes: number;
  /** Percent of attempts followed by a success, per ant; null without attempts. */
  successRate: number | null;
  /** Newest first. */
  events: UnlockEvent[];
}

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj =>
  Boolean(v) && typeof v === 'object' && !Array.isArray(v);
const str = (v: unknown): string =>
  typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '';

export function summarizeUnlocks(data: RentalUnlocks | null | undefined): UnlockSummary {
  const labels: Record<string, string> = {};
  (data?.unlockableHardware ?? []).forEach((h) => {
    labels[h.id] = `${h.hardwareProduct?.name ?? 'Hardware'} #${
      h.numberText ?? h.number ?? ''
    }`;
  });

  const events: UnlockEvent[] = [];
  (data?.unlocks ?? []).forEach((group) => {
    if (!isObj(group)) return;
    const hardwareId = str(group.hardware_id ?? group.hardwareId);
    const list = Array.isArray(group.unlocks) ? group.unlocks : [];
    list.forEach((event, i) => {
      if (!isObj(event)) return;
      const at = str(event.timestamp ?? event.at);
      if (!at) return;
      events.push({
        id: str(event.id) || `${hardwareId}-${i}`,
        at,
        type: str(event.type).toLowerCase(),
        hardwareLabel:
          labels[hardwareId] ?? (hardwareId ? `Unit ${hardwareId.slice(0, 8)}` : ''),
      });
    });
  });
  events.sort((a, b) => (Date.parse(b.at) || 0) - (Date.parse(a.at) || 0));

  const attempts = events.filter((e) => e.type === 'attempt').length;
  return {
    attempts,
    successes: events.filter((e) => e.type === 'success').length,
    successRate:
      typeof data?.unlocksSuccessRate === 'number'
        ? Math.round(data.unlocksSuccessRate)
        : null,
    events,
  };
}
